import React, { useEffect, useState } from "react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X, Save, Image as ImageIcon } from "lucide-react";

const EMPTY_PRODUCT = {
  slug: "",
  name: "",
  tag: "",
  desc: "",
  spec: "",
  full_desc: "",
  image: "",
  applications: [],
  specs: [],
  packaging: [],
  order: 0,
  published: true,
};

const AdminProducts = () => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const openNew = () => setEditing({ ...EMPTY_PRODUCT });
  const openEdit = (p) =>
    setEditing({
      ...EMPTY_PRODUCT,
      ...p,
      applications: p.applications || [],
      specs: p.specs || [],
      packaging: p.packaging || [],
    });

  const save = async () => {
    if (!editing) return;
    const body = { ...editing };
    body.order = Number(body.order) || 0;
    try {
      if (editing.id) {
        await api.patch(`/admin/products/${editing.id}`, body);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", body);
        toast.success("Product created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((p) => ({ ...p, image: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="overline text-[#DE9E36]">Catalogue</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-[#1A201C] tracking-tight">
            Products
          </h1>
          <p className="mt-2 text-sm text-[#4E5750]">{items.length} products in database.</p>
        </div>
        <button
          onClick={openNew}
          data-testid="new-product-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#DE9E36] text-[#1A201C] text-xs tracking-[0.22em] uppercase font-bold hover:bg-[#1A201C] hover:text-[#DE9E36] transition-colors"
        >
          <Plus size={16} /> New product
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-[#4E5750]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-14 border border-dashed border-[#E5E0D8] text-center text-sm text-[#4E5750]">
          No products yet. Click "New product" to add one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((p) => (
            <article
              key={p.id}
              data-testid={`product-${p.slug}`}
              className="border border-[#E5E0D8] bg-white overflow-hidden flex flex-col"
            >
              <div className="h-44 bg-[#F3EFE7] flex items-center justify-center overflow-hidden">
                {p.image ? (
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon size={24} className="text-[#4E5750]/40" />
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  {p.tag && (
                    <span className="px-2 py-0.5 text-[10px] tracking-[0.2em] uppercase bg-[#F3EFE7] text-[#4E5750]">
                      {p.tag}
                    </span>
                  )}
                  <span
                    className={`text-[10px] tracking-[0.2em] uppercase ${
                      p.published ? "text-green-700" : "text-gray-500"
                    }`}
                  >
                    {p.published ? "Published" : "Draft"}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-[#1A201C]">{p.name}</h3>
                <div className="text-xs text-[#4E5750] mt-1 font-mono">{p.slug}</div>
                {p.desc && <p className="text-sm text-[#4E5750] mt-3 line-clamp-3">{p.desc}</p>}
                <div className="mt-auto pt-4 flex gap-2">
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`edit-${p.slug}`}
                    className="inline-flex items-center gap-1 px-3 py-2 text-xs tracking-[0.2em] uppercase border border-[#E5E0D8] hover:bg-[#F3EFE7]"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    className="inline-flex items-center gap-1 px-3 py-2 text-xs tracking-[0.2em] uppercase text-red-700 border border-red-200 hover:bg-red-50"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setEditing(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#FDFBF7] w-full max-w-3xl my-10 max-h-[90vh] overflow-y-auto"
            data-testid="product-editor"
          >
            <div className="sticky top-0 bg-[#1A201C] text-white px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="overline text-[#DE9E36]">
                  {editing.id ? "Edit product" : "New product"}
                </div>
                <div className="text-lg font-bold">{editing.name || "Untitled"}</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 md:p-8 space-y-5">
              {/* Image */}
              <Row>
                <Label>Image</Label>
                <div className="flex items-start gap-4">
                  <div className="w-40 h-40 bg-[#F3EFE7] border border-[#E5E0D8] overflow-hidden flex items-center justify-center">
                    {editing.image ? (
                      <img src={editing.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={24} className="text-[#4E5750]/40" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className="inline-flex items-center gap-2 px-4 py-3 bg-[#1A201C] text-[#DE9E36] text-xs tracking-[0.2em] uppercase cursor-pointer hover:bg-[#DE9E36] hover:text-[#1A201C] transition-colors">
                      <Upload size={14} />
                      {uploading ? "Uploading…" : "Upload file"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => uploadImage(e.target.files?.[0])}
                        data-testid="product-image-upload"
                      />
                    </label>
                    <input
                      type="text"
                      placeholder="Or paste an image URL"
                      value={editing.image || ""}
                      onChange={(e) => setEditing({ ...editing, image: e.target.value })}
                      className="w-full h-10 px-3 border border-[#E5E0D8] bg-white text-sm"
                    />
                  </div>
                </div>
              </Row>

              <div className="grid sm:grid-cols-2 gap-5">
                <Row>
                  <Label>Slug (unique)</Label>
                  <Input
                    value={editing.slug}
                    onChange={(v) => setEditing({ ...editing, slug: v })}
                    placeholder="refined-sunflower-oil"
                    testId="product-slug"
                  />
                </Row>
                <Row>
                  <Label>Name</Label>
                  <Input
                    value={editing.name}
                    onChange={(v) => setEditing({ ...editing, name: v })}
                    testId="product-name"
                  />
                </Row>
                <Row>
                  <Label>Tag</Label>
                  <Input
                    value={editing.tag || ""}
                    onChange={(v) => setEditing({ ...editing, tag: v })}
                    placeholder="Premium"
                  />
                </Row>
                <Row>
                  <Label>Order</Label>
                  <Input
                    value={editing.order}
                    onChange={(v) => setEditing({ ...editing, order: v })}
                    placeholder="0"
                  />
                </Row>
              </div>

              <Row>
                <Label>Short description</Label>
                <Input value={editing.desc || ""} onChange={(v) => setEditing({ ...editing, desc: v })} />
              </Row>

              <Row>
                <Label>Spec summary</Label>
                <Input value={editing.spec || ""} onChange={(v) => setEditing({ ...editing, spec: v })} />
              </Row>

              <Row>
                <Label>Full description</Label>
                <textarea
                  value={editing.full_desc || ""}
                  onChange={(e) => setEditing({ ...editing, full_desc: e.target.value })}
                  rows={4}
                  className="w-full p-3 border border-[#E5E0D8] bg-white text-sm"
                />
              </Row>

              <ListEditor
                label="Applications"
                items={editing.applications}
                onChange={(v) => setEditing({ ...editing, applications: v })}
              />

              <SpecsEditor
                items={editing.specs}
                onChange={(v) => setEditing({ ...editing, specs: v })}
              />

              <ListEditor
                label="Packaging"
                items={editing.packaging}
                onChange={(v) => setEditing({ ...editing, packaging: v })}
              />

              <Row>
                <label className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={!!editing.published}
                    onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                    className="w-4 h-4"
                  />
                  Published
                </label>
              </Row>
            </div>

            <div className="sticky bottom-0 bg-[#FDFBF7] border-t border-[#E5E0D8] px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setEditing(null)}
                className="px-5 py-3 text-xs tracking-[0.22em] uppercase border border-[#E5E0D8]"
              >
                Cancel
              </button>
              <button
                onClick={save}
                data-testid="save-product"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#DE9E36] text-[#1A201C] text-xs tracking-[0.22em] uppercase font-bold hover:bg-[#1A201C] hover:text-[#DE9E36] transition-colors"
              >
                <Save size={14} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ children }) => <div className="space-y-2">{children}</div>;
const Label = ({ children }) => (
  <div className="overline text-[#4E5750]">{children}</div>
);
const Input = ({ value, onChange, placeholder, testId }) => (
  <input
    type="text"
    value={value}
    placeholder={placeholder}
    data-testid={testId}
    onChange={(e) => onChange(e.target.value)}
    className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:border-[#DE9E36]"
  />
);

const ListEditor = ({ label, items, onChange }) => {
  const set = (i, v) => {
    const copy = [...items];
    copy[i] = v;
    onChange(copy);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);
  return (
    <Row>
      <Label>{label}</Label>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              value={it}
              onChange={(e) => set(i, e.target.value)}
              className="flex-1 h-10 px-3 border border-[#E5E0D8] bg-white text-sm"
            />
            <button
              onClick={() => remove(i)}
              className="px-3 border border-[#E5E0D8] hover:bg-red-50 text-red-700"
              type="button"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#DE9E36]"
        >
          <Plus size={12} /> Add
        </button>
      </div>
    </Row>
  );
};

const SpecsEditor = ({ items, onChange }) => {
  const set = (i, key, val) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [key]: val };
    onChange(copy);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { k: "", v: "" }]);
  return (
    <Row>
      <Label>Technical specifications</Label>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input
              placeholder="Label"
              value={it.k}
              onChange={(e) => set(i, "k", e.target.value)}
              className="flex-1 h-10 px-3 border border-[#E5E0D8] bg-white text-sm"
            />
            <input
              placeholder="Value"
              value={it.v}
              onChange={(e) => set(i, "v", e.target.value)}
              className="flex-1 h-10 px-3 border border-[#E5E0D8] bg-white text-sm"
            />
            <button
              onClick={() => remove(i)}
              className="px-3 border border-[#E5E0D8] hover:bg-red-50 text-red-700"
              type="button"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#DE9E36]"
        >
          <Plus size={12} /> Add spec
        </button>
      </div>
    </Row>
  );
};

export default AdminProducts;
